import { Record as record, List } from 'immutable';
import moment from 'moment';
import { get, sortBy } from 'lodash';
import config from '../../config';

import { resolve } from '../../helpers/LinkMapper';
import OrderTripFares from './TripFares';
import { OrderTripTypes, TripBookingStatus } from '../../constants/AppConstants';
import trainTime from '../../lib/TrainTime';
import LeadPassenger from './LeadPassenger';
import Station from '../Station';
import FavouritesStore from '../../stores/FavouritesStore';
import FeatureFlagStore from '../../stores/FeatureFlagStore';
import { annotateFareLegsWithTripId } from '../../helpers/Fares';

const defaults = {
    id: null,
    bookingStatus: null,
    bookingReference: null,
    fares: new OrderTripFares(),
    itso: false,
    selfPrintPassenger: new LeadPassenger(),
    isSeatsRequested: false,
    splitSalt: null,
    outwardSplitReferenceId: null,
    returnSplitReferenceId: null,
};


class OrderTrip extends record(defaults) {

    getTripId() {
        const journeyFares = this.getJourneyFares();
        return get(journeyFares, '[0].tripId', null);
    }

    isSmartcard () {
        return this.itso;
    }

    getType () {
        if (this.hasDaySaveFare()) { return OrderTripTypes.DAYSAVE; }
        if (this.hasRoversRangersFare()) { return OrderTripTypes.ROVERSRANGERS; }
        if (this.fares.season) { return OrderTripTypes.SEASON; }
        if (this.fares.return) { return OrderTripTypes.RETURN; }
        if (this.hasFareOnlyFare()) { return OrderTripTypes.FARE_ONLY; }
        if (this.fares.outwardSingle && this.fares.returnSingle) { return OrderTripTypes.RETURN_SINGLES; }
        if (this.fares.returnSingle) { return OrderTripTypes.RETURN_SINGLE; }

        return OrderTripTypes.SINGLE;
    }

    isSingle () {
        return this.getType() === OrderTripTypes.SINGLE;
    }

    isFareOnly () {
        return this.getType() === OrderTripTypes.FARE_ONLY;
    }

    isSeason () {
        return this.getType() === OrderTripTypes.SEASON;
    }

    canAmend () {
        const minsFromNow = moment().add(config.get('amendMinMinutesLeft'), 'minutes');
        const hasValidFares = this.getProducts().some((fare) => {
            const isAdvance = FeatureFlagStore.get('enableAdvanceToWalkUpAmends') || fare.isAdvance();
            return isAdvance &&
                fare.hasAmendableTickets() &&
                fare.getDepartureTime() &&
                fare.getDepartureTime().isAfter(minsFromNow);
        });
        return !this.isSeason() && hasValidFares;
    }

    needsJourney () {
        const type = this.getType();
        return type !== OrderTripTypes.DAYSAVE && type !== OrderTripTypes.SEASON
            && type !== OrderTripTypes.ROVERSRANGERS && !this.isTransPennineStaffRover();
    }

    hasFare (fare) {
        return this.getJourneyFares().contains(fare);
    }

    getJourneyFares () {
        const fares = this.fares;

        let journeyFares = new List();
        switch (this.getType()) {
            case OrderTripTypes.DAYSAVE:
                journeyFares = List.of(this.getDaySaveFare());
                break;
            case OrderTripTypes.ROVERSRANGERS:
                journeyFares = List.of(this.getRoversRangersFare());
                break;
            case OrderTripTypes.FARE_ONLY:
                journeyFares = List.of(this.getFareOnlyFare());
                break;
            case OrderTripTypes.RETURN:
                journeyFares = List.of(fares.return);
                break;
            case OrderTripTypes.RETURN_SINGLES:
                journeyFares = List.of(fares.outwardSingle, fares.returnSingle);
                break;
            case OrderTripTypes.RETURN_SINGLE:
                journeyFares = List.of(fares.returnSingle);
                break;
            case OrderTripTypes.SEASON:
                journeyFares = List.of(fares.season);
                break;
            default:
                journeyFares = List.of(fares.outwardSingle);
        }

        return journeyFares.filter((x) => !!x);
    }

    getFareByTicketId (id) {
        return this.getJourneyFares().concat(this.getPlusBusFares()).find(fare => fare.hasTicket(id));
    }

    getFaresIncludedInFavourites (withDelivery = false) {
        const favourites = FavouritesStore.getFavourites();
        return this.getJourneyFares().filter(fare =>
            favourites.filter(favourite =>
                favourite.matchFare(fare, this) && (
                    withDelivery ? favourite.delivery : true
                )
            ).isEmpty() === false
        );
    }

    includesFavouriteFare (withDelivery = false) {
        return this.getFaresIncludedInFavourites(withDelivery).size > 0;
    }

    getPhotocardNumber () {
        if (!this.hasSeasonFare) { return null; }

        const seasonFare = this.getSeasonFare();
        if (!seasonFare.hasPhotocardNumber()) { return null; }

        return seasonFare.getPhotocardNumber();
    }

    hasPhotocardNumber () {
        return !!this.getPhotocardNumber();
    }

    getTotalPrice () {
        const fares = this.getJourneyFares().reduce((out, fare) => out + fare.getTotalPrice(), 0);
        const plusBus = this.hasPlusBus() ? this.getPlusBus().getTotalPrice() : 0;

        return fares + plusBus;
    }

    getTotalPriceWithoutDiscount () {
        const fares = this.getJourneyFares().reduce((out, fare) => out + fare.getTotalPriceWithoutDiscount(), 0);
        const plusBus = this.hasPlusBus() ? this.getPlusBus().getTotalPrice() : 0;

        return fares + plusBus;
    }

    getDepartureTime () {
        // A trip with only a return-direction fare (e.g. a "regular return single" split
        // leg) has no outward journey - fall back to the return journey it does have.
        const journey = this.getOutwardJourney() || this.getReturnJourney();
        return journey ? journey.getDepartureTime() : null;
    }

    getEarliestTime () {
        const journey = this.getOutwardJourney() || this.getReturnJourney();
        if (!journey) {
            return null;
        }
        return this.needsJourney() && journey.getDepartureTime() || journey.validFrom;
    }

    getOutwardJourney () {
        const fare = this.getOutwardFare();
        return fare?.outwardJourney || null;
    }

    getReturnJourney () {
        if (this.fares.returnSingle) {
            return this.fares.returnSingle.outwardJourney;
        }

        if (this.fares.return) {
            return this.fares.return.returnJourney;
        }

        return null;
    }

    hasReturnJourney () {
        return !!this.getReturnJourney();
    }

    getJourneys () {
        return new List([
            this.getOutwardJourney(),
            this.getReturnJourney()
        ]).filter(Boolean);
    }

    getCleanId () {
        let regex = /orders\/.*\/trips\/(.*)/;
        let matches = this.id.match(regex);
        if (matches) {
            return matches[1];
        }
        return this.id;
    }

    getPlusBus () {
        return this.fares.plusBus;
    }

    getPlusBusFares () {
        return this.fares.plusBus.getFares();
    }

    hasPlusBus () {
        return !!(this.fares.plusBus && this.getPlusBus().count())
            && this.getPlusBusFares()
            .some(fare => fare.getPlusBusTickets()
                  .some(ticket => !ticket.isRefunded));
    }

    getDaySaveFare () {
        return this.fares.getDaySaveFare();
    }

    getRoversRangersFare () {
        return this.fares.getRoversRangersFare();
    }

    hasDaySaveFare () {
        return !!this.getDaySaveFare();
    }

    hasRoversRangersFare () {
        return !!this.getRoversRangersFare();
    }

    hasFareOnlyFare () {
        return !!this.getFareOnlyFare();
    }

    getFareOnlyFare () {
        return this.fares.fareOnly;
    }

    hasSeasonFare () {
        return !!this.getSeasonFare();
    }

    getSeasonFare () {
        return this.fares.season;
    }

    setRoversRangersFare (fare) {
        return this.setIn(['fares', 'roversRangers'], fare);
    }

    getOutwardFare () {
        if (this.hasFareOnlyFare()) {
            return this.getFareOnlyFare();
        }
        if (this.hasDaySaveFare()) {
            return this.getDaySaveFare();
        }
        if (this.hasRoversRangersFare()) {
            return this.getRoversRangersFare();
        }
        if (this.hasSeasonFare()) {
            return this.getSeasonFare();
        }
        if (this.fares.return) {
            return this.fares.return;
        }

        if (this.fares.outwardSingle) {
            return this.fares.outwardSingle;
        }
        return null;

    }

    seatsReserved () {
        return this.bookingStatus === TripBookingStatus.OK;
    }

    seatsNotRequired () {
        return this.bookingStatus === TripBookingStatus.FAILED_NOT_REQUIRED;
    }

    isPrinted () {
        if (this.isSmartcard()) {
            return undefined;
        }
        const fares = this.getJourneyFares();
        return fares.every((fare) => fare.isPrinted());
    }

    isUnprinted () {
        if (this.isSmartcard()) {
            return undefined;
        }
        const fares = this.getJourneyFares();
        return fares.some((fare) => fare.isUnprinted());
    }

    // this method does not consider eTickets:
    isTooSoonToCollect () {
        if (this.isSmartcard()) {
            return trainTime().add(2, 'hours').isAfter(this.getDepartureTime());
        }
        return trainTime().add(30, 'minutes').isAfter(this.getDepartureTime());
    }

    hasTicket (id) {
        return this.amendedTickets.some(fare => fare.newTicket === id);
    }

    isReturn () {
        return !!this.getOutwardJourney() && !!this.getReturnJourney();
    }

    isOpenReturn () {
        return this.fares.isOpenReturn();
    }

    getTickets () {
        return this.getJourneyFares().map((fare) => fare && fare.tickets).flatten(true);
    }

    getTicketsWithTicketTypeAndValidity () {
        return this.getJourneyFares().map((fare) => fare &&
            fare.tickets
                .map(t => t.set('ticketType', fare.ticketType.code))
                .map(t => t.set('travelStartDate', fare.getValidFrom()))
            ).flatten(true);
    }

    getProducts () {
        return this.getJourneyFares().concat(this.getPlusBus().getFares());
    }

    hasRefundableTicket () {
        return this.getTickets().some((t) => t.isRefundable)
            || this.getPlusBusFares().some(f => f.hasRefundableTickets());
    }

    hasDownloadableTickets () {
        return this.getTickets().some((t) => t.attachment);
    }

    hasJCPRailcard () {
        return this.getTickets().some((t) => t.railcard && t.railcard.code === 'JCP');
    }

    has1617Railcard () {
        return this.getTickets().some((t) => t.railcard && t.railcard.code === 'TSU');
    }

    getFirstOrigin () {
        if (this.hasFareOnlyFare()) {
            return this.getFareOnlyFare().origin;
        }

        if (this.hasDaySaveFare() || this.hasRoversRangersFare()) {
            return new Station();
        }

        if (this.hasSeasonFare()) {
            return this.getSeasonFare().origin;
        }

        const journey = this.getOutwardJourney();
        const firstLeg = journey && journey.legs.first();
        if (firstLeg) {
            return firstLeg.origin;
        }

        const fare = this.getOutwardFare();
        return fare && fare.origin || new Station(); // Open Golf does not have legs use origin from the fare instead
    }

    getDestination () {
        const journey = this.getOutwardJourney();
        if (journey && journey.legs.size) {
            return journey.legs.last().destination;
        }
        const fare = this.getOutwardFare();
        return fare && fare.destination || new Station();
    }

    needsPassengers () {
        return this.hasRoversRangersFare();
    }

    hasPassengers () {
        return this.getTickets().every((ticket) => ticket.passenger !== null);
    }

    hasDiscount (discount) {
        return this.getTickets().some( ticket => ticket.hasDiscount(discount));
    }

    canClaimDelayRepay() {
        // Return false for open return and other trip types
        const notApplicable = [
            OrderTripTypes.SEASON,
            OrderTripTypes.DAYSAVE,
            OrderTripTypes.ROVERSRANGERS,
        ];
        if (this.isOpenReturn() || notApplicable.includes(this.getType())) {
            return false;
        }

        const outwardFare = this.getOutwardFare();
        const returnFare = this.fares.returnSingle || this.fares.return;
        const hasClaimableTickets = fare => fare && fare.tickets.some(ticket => !ticket.refunded);

        const journeys = [
            hasClaimableTickets(outwardFare) ? outwardFare.outwardJourney : null,
            hasClaimableTickets(returnFare)
                ? returnFare.isReturn()
                    ? returnFare.returnJourney
                    : returnFare.outwardJourney
                : null,
        ];

        // Can only claim for fares with journeys that might have been delayed (in the past)
        const pastJourneys = journeys.filter(
            journey => {
                const departure = journey && journey.getDepartureTime();
                return (
                    departure &&
                    departure.isBefore() &&
                    departure.isAfter(
                        trainTime()
                            .startOf('day')
                            .subtract(config.get('delayRepayMaxTicketDays'), 'days'),
                    )
                );
            }
        );

        if (pastJourneys.length >= 0) {
            // At least one leg in the journey must operate on a valid TOC for this brand
            const tocGroup = config.get('tocGroup');
            const isApplicableToBrand = pastJourneys.some(journey => {
                const legs = journey.legs.filter(leg => leg.toc);
                return legs.some(leg => leg.toc && tocGroup.includes(leg.toc.code));
            });
            return isApplicableToBrand;
        }

        return false;
    }

    isFlexi () {
        return this.fares.season && this.fares.season.smartTicketType === 'FL1';
    }

    isTransPennineStaffRover() {
        const origin = this.getFirstOrigin();
        const destination = this.getDestination();
        return Boolean(
            origin && destination &&
            origin.get('nlc') === 'Z031' &&
            destination.get('nlc') === 'Z032'
        );
    }

    getJourneyFaresSortedByFareId() {
        const journeyFaresArray = Array.from(this.getJourneyFares());
        return sortBy(
            journeyFaresArray.filter((fare) => !fare.isPlusBus()),
            'fareId',
        );
    }

    setAmendableTrip (orderTrip) {
        let amendableTrip = orderTrip;
        if (orderTrip.fares.outwardSingle && !orderTrip.fares.outwardSingle.isAdvance()) {
            amendableTrip = amendableTrip
                .setIn(['fares', 'outwardSingle'], null)
                .setIn(['fares', 'plusBus', 'outward'], null);

        }
        if (orderTrip.fares.returnSingle && !orderTrip.fares.returnSingle.isAdvance()) {
            amendableTrip = amendableTrip
                .setIn(['fares', 'returnSingle'], null)
                .setIn(['fares', 'plusBus', 'return'], null);
        }
        return amendableTrip;
    }

    getTotalMaxAdults() {
        return this.getJourneyFares().reduce((acc, fare) => Math.max(acc, fare.adults()), 0);
    }

    getTotalMaxChildren() {
        return this.getJourneyFares().reduce(
            (acc, fare) => Math.max(acc, fare.children()),
            0,
        );
    }

    isPartOfSplitAsSingleReturn() {
        return !!this.outwardSplitReferenceId || (!this.isSplitReturn() && !!this.getFareSplitId());
    }

    isPartOfSplitAsSingleOutward() {
        return !!this.returnSplitReferenceId || (!this.isSplitReturn() && !!this.getFareSplitId());
    }

    isSplitReturn() {
        const fares = this.getJourneyFares();
        return Array.isArray(fares) && fares.length > 0 && fares.every((fare) => fare.isSplitReturn());
    }

    getSplitId() {
        return this.getFareSplitId() || this.outwardSplitReferenceId || this.returnSplitReferenceId || null;
    }

    isSplitTrip() {
        return !!this.getSplitId();
    }

    getFareSplitId() {
        const fares = this.getJourneyFares();
        const fare = fares?.find(f => f?.isPartOfSplit());
        return fare?.splitId || null;
    }

    isPartOfParticularSplit(splitId, splitSalt) {
        if (!splitId || splitSalt !== this.splitSalt) {
            return false;
        }
        return (
            this.getFareSplitId() === splitId ||
            this.outwardSplitReferenceId === splitId ||
            this.returnSplitReferenceId === splitId
        );
    }

    isPartOfSplitAsRegularOutwardSingle() {
        return !!this.returnSplitReferenceId;
    }

    isPartOfSplitAsRegularReturnSingle() {
        return !!this.outwardSplitReferenceId;
    }

    isPartOfSplitAsReturn() {
        return this.isSplitReturn() && !!this.getFareSplitId();
    }

    static fromJSON (json) {

        // Preprocess fares for split ticketing
        if (json.fares) {
            json.fares = annotateFareLegsWithTripId(json.fares);
        }

        return new OrderTrip({
            id: json.id,
            bookingStatus: json.bookingStatus,
            bookingReference: json.bookingReference,
            fares: resolve(json.fares).as(OrderTripFares),
            itso: resolve(json.itso).with(Boolean),
            selfPrintPassenger: resolve(json.selfPrintPassenger).as(LeadPassenger) || new LeadPassenger(),
            isSeatsRequested: json.isSeatsRequested,
            splitSalt: json.splitSalt,
            outwardSplitReferenceId: json.outwardSplitReferenceId,
            returnSplitReferenceId: json.returnSplitReferenceId,
        });
    }
}

export default OrderTrip;
